import type { LeadJourneyLink } from "api/crm-analytics";
import { JOURNEY_GEOMETRY as G } from "./constants";
import { linkId, type JourneyGraph, type JourneyNode } from "./graph";

/** Where a node's pill hangs relative to its bar. */
export type PillAnchor = "corner" | "before" | "after";

export type LaidOutNode = JourneyNode & {
  x: number;
  y: number;
  height: number;
  pillAnchor: PillAnchor;
};

export type LaidOutLink = {
  id: string;
  link: LeadJourneyLink;
  /** SVG path for the ribbon. */
  path: string;
  fromId: string;
  toId: string;
};

type JourneyLayout = {
  width: number;
  height: number;
  nodes: LaidOutNode[];
  links: LaidOutLink[];
};

type Working = {
  node: JourneyNode;
  x: number;
  y: number;
  height: number;
  /** Ribbons leaving / entering, with their stacking offsets on this node. */
  out: WorkingLink[];
  in: WorkingLink[];
};

type WorkingLink = {
  link: LeadJourneyLink;
  height: number;
  /** y at the source end and at the target end. */
  y0: number;
  y1: number;
  /** y of each column this skip ribbon arches over, keyed by column index. */
  lanes: Map<number, number>;
};

const EMPTY_LAYOUT: JourneyLayout = {
  width: 0,
  height: 0,
  nodes: [],
  links: []
};

// Ribbons stack on a node in reading order: skips arriving from further back
// first, then the onward step, then the drop-offs.
const OUT_ORDER: Record<LeadJourneyLink["kind"], number> = {
  skip: 0,
  source: 1,
  next: 1,
  drop_off: 2
};

const RENDER_ORDER: Record<LeadJourneyLink["kind"], number> = {
  source: 0,
  next: 1,
  drop_off: 2,
  skip: 3
};

const formatRate = (rate: number | null): string => {
  if (rate === null) return "";
  if (rate > 0 && rate < 0.005) return "<1%";
  return `${Math.round(rate * 100)}%`;
};

/**
 * Width of a pill, measured rather than guessed: the gap between two columns
 * has to fit the pills that live in it, or labels overlap the next column.
 * Falls back to a per-character estimate where canvas measurement is
 * unavailable (jsdom, or a browser that refuses the 2d context).
 */
const AVERAGE_GLYPH_WIDTH = 6.4;
const PILL_CHROME_WIDTH = 46;

let measureContext: CanvasRenderingContext2D | null | undefined;

const textWidth = (text: string): number => {
  if (measureContext === undefined) {
    measureContext =
      typeof document === "undefined"
        ? null
        : document.createElement("canvas").getContext("2d");
    if (measureContext)
      measureContext.font =
        '500 12px Satoshi, -apple-system, "Segoe UI", system-ui, sans-serif';
  }
  if (!measureContext) return text.length * AVERAGE_GLYPH_WIDTH;
  return measureContext.measureText(text).width;
};

const pillWidth = (node: JourneyNode): number => {
  const rate = formatRate(node.rateOfPrevious);
  return (
    PILL_CHROME_WIDTH +
    textWidth(node.label) +
    textWidth(node.count.toLocaleString("en-US")) +
    (rate ? textWidth(rate) + 6 : 0)
  );
};

const pillAnchorFor = (node: JourneyNode): PillAnchor => {
  if (node.kind === "source") return "after";
  if (node.kind === "outcome") return "before";
  // Every milestone labels at its own top-right corner, the last one included.
  // The layout reserves a trailing gutter for it rather than flipping it to
  // the left, where it would be drawn on top of the ribbon that feeds it.
  return "corner";
};

type ColumnIndex = (node: JourneyNode) => number;

/**
 * Column each node's bar sits in. Sources occupy column 0, then one column per
 * milestone in order; a drop-off band sits one column further right than the
 * milestone it left, directly under the next milestone.
 *
 * Milestones nobody reached are skipped rather than given a column of their
 * own. They draw no bar and carry no ribbons, so the column would hold nothing
 * — but it still costs its full width, and the trunk then has to cross a wide
 * empty stretch to reach the next step that does have flow. On the reported
 * cohort that was two dead columns in the middle of the chart.
 *
 * Columns are therefore indexed off the milestones that are actually drawn,
 * not off `rank`, which keeps its gaps.
 */
const buildColumnIndex = (graph: JourneyGraph): ColumnIndex => {
  const columnByRank = new Map<number, number>();
  graph.milestones
    .filter((milestone) => milestone.count > 0)
    .forEach((milestone, index) => columnByRank.set(milestone.rank, index + 1));

  return (node) => {
    if (node.kind === "source") return 0;
    // An outcome always hangs off a milestone that has flow, so its lookup
    // never misses; an empty milestone falls back to 0 and is not drawn.
    const column = columnByRank.get(node.rank) ?? 0;
    return node.kind === "milestone" ? column : column + 1;
  };
};

/** A milestone nobody reached draws nothing at all. */
const isDrawn = (node: JourneyNode): boolean =>
  node.count > 0 || (node.kind !== "milestone" && node.kind !== "source");

/**
 * Gap a node's pill has to fit into. A corner pill hangs to the right of its
 * bar and so belongs to the gap after its column; a "before" pill hangs to the
 * left and belongs to the gap in front of it.
 */
const pillGapOf = (
  node: JourneyNode,
  anchor: PillAnchor,
  columnOf: ColumnIndex
): number => (anchor === "before" ? columnOf(node) - 1 : columnOf(node));

const curveTo = (x0: number, y0: number, x1: number, y1: number): string => {
  const mid = (x0 + x1) / 2;
  return `C${mid},${y0} ${mid},${y1} ${x1},${y1}`;
};

/**
 * Builds the ribbon outline: down the leading edge with cubic curves, across
 * the far end, and back along the trailing edge. A skip ribbon threads through
 * its lanes as straight segments so it reads as a bridge over the milestones
 * it bypasses rather than a wave behind them.
 */
const ribbonPath = (
  entry: WorkingLink,
  from: Working,
  to: Working,
  columnX: number[]
): string => {
  const points: { x: number; y: number; straight: boolean }[] = [
    { x: from.x + G.nodeWidth, y: entry.y0, straight: false }
  ];
  [...entry.lanes.keys()]
    .sort((a, b) => a - b)
    .forEach((column) => {
      const laneY = entry.lanes.get(column)!;
      const x = columnX[column];
      points.push({ x, y: laneY, straight: false });
      points.push({ x: x + G.nodeWidth, y: laneY, straight: true });
    });
  points.push({ x: to.x, y: entry.y1, straight: false });

  const segments = points.slice(1).map((point, index) => ({
    from: points[index],
    to: point,
    // A segment is drawn straight when it runs along a lane, i.e. when the
    // point that opened it was a lane entry.
    straight: points[index].straight
  }));

  let path = `M${points[0].x},${points[0].y}`;
  segments.forEach((segment) => {
    path += segment.straight
      ? `L${segment.to.x},${segment.to.y}`
      : curveTo(segment.from.x, segment.from.y, segment.to.x, segment.to.y);
  });

  const end = points[points.length - 1];
  path += `L${end.x},${end.y + entry.height}`;
  for (let i = segments.length - 1; i >= 0; i -= 1) {
    const segment = segments[i];
    path += segment.straight
      ? `L${segment.from.x},${segment.from.y + entry.height}`
      : curveTo(
          segment.to.x,
          segment.to.y + entry.height,
          segment.from.x,
          segment.from.y + entry.height
        );
  }
  return `${path}Z`;
};

/**
 * Columns a skip ribbon bridges: the milestones strictly between its two ends
 * that leads actually reached. An unreached milestone has no column at all, so
 * there is nothing there to clear.
 */
const bridgedColumns = (
  graph: JourneyGraph,
  entry: WorkingLink,
  columnOf: ColumnIndex
): number[] => {
  const from = graph.nodeById.get(entry.link.from_id);
  const to = graph.nodeById.get(entry.link.to_id);
  if (!from || !to) return [];

  const columns: number[] = [];
  for (let rank = from.rank + 1; rank < to.rank; rank += 1) {
    const milestone = graph.milestones[rank];
    if ((milestone?.count ?? 0) > 0) columns.push(columnOf(milestone));
  }
  return columns;
};

const spanOf = (graph: JourneyGraph, entry: WorkingLink): number => {
  const from = graph.nodeById.get(entry.link.from_id);
  const to = graph.nodeById.get(entry.link.to_id);
  return (to?.rank ?? 0) - (from?.rank ?? 0);
};

/**
 * Where each column starts, and how wide the chart ends up.
 *
 * The column count is read off the graph rather than assumed from the
 * milestone list: a drop-off band sits one column right of its milestone, so a
 * cohort that branches off the last milestone needs a column the milestones
 * alone do not account for. Getting that wrong parks those bands back at the
 * left padding, on top of the first column.
 */
const measureColumns = (
  graph: JourneyGraph,
  containerWidth: number,
  columnOf: ColumnIndex
): { columnX: number[]; width: number } => {
  const drawn = graph.nodes.filter(isDrawn);
  const columnCount =
    drawn.reduce((widest, node) => Math.max(widest, columnOf(node)), 0) + 1;
  // One entry per column: the space to its right. The last is the trailing
  // gutter the right-most pill hangs in — it has to fit that pill but no
  // ribbon, so it starts at the padding and takes no share of the slack.
  const gaps: number[] = Array.from({ length: columnCount }, (_, index) =>
    index === columnCount - 1 ? G.paddingX : G.minColumnGap
  );
  drawn.forEach((node) => {
    const gap = pillGapOf(node, pillAnchorFor(node), columnOf);
    if (gap < 0 || gap >= gaps.length) return;
    gaps[gap] = Math.max(gaps[gap], pillWidth(node) + G.nodeWidth + 8);
  });

  const minWidth = G.paddingX + gaps.reduce((a, b) => a + b, 0) + G.nodeWidth;
  const spread = gaps.slice(0, -1);
  // Any width the container has beyond the minimum is shared out evenly, so a
  // narrow chart on a wide screen spreads instead of hugging the left edge.
  const slack = spread.length
    ? Math.max(containerWidth - minWidth, 0) / spread.length
    : 0;

  const columnX: number[] = [G.paddingX];
  spread.forEach((gap, index) => columnX.push(columnX[index] + gap + slack));
  return {
    columnX,
    width: columnX[columnX.length - 1] + G.nodeWidth + gaps[columnCount - 1]
  };
};

/**
 * Positions every bar and ribbon of the Sankey.
 *
 * Pure and deterministic: it takes the graph and the width available and
 * returns coordinates, so it can be memoized on those two inputs and stays
 * testable without a DOM. Heights are proportional throughout — the first
 * milestone is drawn at a fixed height and everything else scales against it.
 */
export const layoutJourney = (
  graph: JourneyGraph,
  containerWidth: number
): JourneyLayout => {
  if (graph.total <= 0 || graph.milestones.length === 0) return EMPTY_LAYOUT;

  const columnOf = buildColumnIndex(graph);
  const { columnX, width } = measureColumns(graph, containerWidth, columnOf);
  const scale = G.rootHeight / graph.total;
  const nodeHeight = (count: number) =>
    count > 0 ? Math.max(count * scale, G.minNodeHeight) : 0;
  const ribbonHeight = (count: number) =>
    count > 0 ? Math.max(count * scale, G.minRibbonHeight) : 0;

  const working = new Map<string, Working>();
  graph.nodes.forEach((node) =>
    working.set(node.id, {
      node,
      x: columnX[columnOf(node)] ?? G.paddingX,
      y: 0,
      height: nodeHeight(node.count),
      out: [],
      in: []
    })
  );

  const entries: WorkingLink[] = graph.links.map((link) => ({
    link,
    height: ribbonHeight(link.count),
    y0: 0,
    y1: 0,
    lanes: new Map<number, number>()
  }));
  entries.forEach((entry) => {
    working.get(entry.link.from_id)?.out.push(entry);
    working.get(entry.link.to_id)?.in.push(entry);
  });

  // Skip ribbons arch over the milestones they bypass, each in its own lane so
  // two of them never share space. The arch costs headroom above the chart and
  // every skip gets one: the only disqualifier is having nothing to bridge.
  //
  // The alternative — letting a skip run flat behind the bars — is what a
  // generic Sankey does, and it is wrong here. These columns are funnel stages,
  // so a bypassing ribbon runs parallel to the trunk rather than away from it,
  // and the two then occupy the same band for the whole width of the chart.
  // On an all-progressed cohort that turned the whole card into one solid
  // slab with the skips darkening it.
  const skipsOverColumn = new Map<number, WorkingLink[]>();
  const archStack = new Map<number, number>();
  let tallestArch = 0;
  entries
    .filter((entry) => entry.link.kind === "skip")
    .sort((a, b) => a.height - b.height || spanOf(graph, a) - spanOf(graph, b))
    .forEach((entry) => {
      const columns = bridgedColumns(graph, entry, columnOf);
      if (columns.length === 0) return;

      const rise = entry.height + G.skipLaneGap;

      columns.forEach((column) => {
        const stacked = (archStack.get(column) ?? 0) + rise;
        archStack.set(column, stacked);
        tallestArch = Math.max(tallestArch, stacked);
        skipsOverColumn.set(column, [
          ...(skipsOverColumn.get(column) ?? []),
          entry
        ]);
      });
    });
  // Longest bridge on the outside, so shorter ones nest underneath it.
  skipsOverColumn.forEach((lane) =>
    lane.sort((a, b) => spanOf(graph, b) - spanOf(graph, a))
  );

  const spine = 8 + G.pillHeight + (tallestArch > 0 ? tallestArch + 16 : 0) + 6;

  // Sources stack on the left, far enough apart that their pills never touch.
  const sources = graph.sources.filter((s) => s.count > 0);
  let previousSource: Working | null = null;
  sources.forEach((source) => {
    const entry = working.get(source.id)!;
    entry.y = previousSource
      ? Math.max(
          previousSource.y + previousSource.height + 6,
          previousSource.y +
            previousSource.height / 2 +
            G.bandPitch -
            entry.height / 2
        )
      : 0;
    previousSource = entry;
  });

  const sourcesHeight = previousSource
    ? (previousSource as Working).y + (previousSource as Working).height
    : 0;
  const root = working.get(graph.milestones[0].id)!;
  // Centre the source stack on the first milestone, then nudge the whole thing
  // down if that would push the topmost pill off the canvas.
  const sourceTop = spine + (root.height - sourcesHeight) / 2;
  const shift = Math.max(0, 22 - sourceTop);
  sources.forEach((source) => {
    const entry = working.get(source.id)!;
    entry.y += sourceTop + shift;
  });

  const milestoneY = spine + shift;
  graph.milestones.forEach((milestone) => {
    working.get(milestone.id)!.y = milestoneY;
  });

  // Deepest thing drawn so far. The milestone bars count: a cohort where every
  // lead progressed has no drop-off bands hanging under them, and the tallest
  // bar is then the only thing deciding how tall the chart has to be.
  let lowestPoint = graph.milestones.reduce(
    (lowest, milestone) =>
      Math.max(
        lowest,
        milestoneY +
          Math.max(working.get(milestone.id)!.height, G.minNodeHeight)
      ),
    sourcesHeight + sourceTop + shift
  );

  // Lanes for the skip ribbons, stacked upward from just above the pills.
  skipsOverColumn.forEach((lane, column) => {
    let cursor = milestoneY - (G.pillHeight + 14);
    [...lane].reverse().forEach((entry) => {
      entry.lanes.set(column, cursor - entry.height);
      cursor -= entry.height + G.skipLaneGap;
    });
  });

  // The bar standing in each column, so a band can clear the one it is drawn
  // under as well as the one it left.
  const milestoneByColumn = new Map<number, Working>();
  graph.milestones.forEach((milestone) => {
    if (milestone.count > 0)
      milestoneByColumn.set(columnOf(milestone), working.get(milestone.id)!);
  });

  // Drop-off bands hang below the milestone they branch into, stacked in the
  // order the server sent them.
  graph.milestones.forEach((milestone) => {
    const milestoneEntry = working.get(milestone.id)!;
    // A band sits one column right of its own milestone — directly under the
    // next one — so it has to clear whichever of the two reaches lower. In a
    // funnel that only ever narrows, that is always its own milestone; but a
    // cohort that skips a step can arrive at the next milestone *wider* than
    // the one it branched off, and then the band lands inside that taller bar.
    const underBar = milestoneByColumn.get(columnOf(milestone) + 1);
    const floor = Math.max(
      milestoneEntry.y + Math.max(milestoneEntry.height, G.minNodeHeight),
      underBar ? underBar.y + Math.max(underBar.height, G.minNodeHeight) : 0
    );

    let previous: Working | null = null;
    graph.outcomes
      .filter((outcome) => outcome.rank === milestone.rank)
      .forEach((outcome) => {
        const entry = working.get(outcome.id)!;
        entry.y = previous
          ? Math.max(
              previous.y + previous.height + G.bandGap,
              previous.y + previous.height / 2 + G.bandPitch - entry.height / 2
            )
          : Math.max(floor + G.bandGap, floor + 22 - entry.height / 2);
        previous = entry;
        lowestPoint = Math.max(lowestPoint, entry.y + entry.height);
      });
  });

  // Stack the ribbon ends on each bar: onward first, drop-offs stepping down.
  working.forEach((entry) => {
    let cursor = entry.y;
    entry.out
      .sort((a, b) => outOrder(graph, a) - outOrder(graph, b))
      .forEach((link) => {
        link.y0 = cursor;
        cursor += link.height;
      });
    cursor = entry.y;
    entry.in
      .sort((a, b) => inOrder(graph, a) - inOrder(graph, b))
      .forEach((link) => {
        link.y1 = cursor;
        cursor += link.height;
      });
  });

  const nodes: LaidOutNode[] = graph.nodes.filter(isDrawn).map((node) => {
    const entry = working.get(node.id)!;
    return {
      ...node,
      x: entry.x,
      y: entry.y,
      height:
        node.count > 0
          ? Math.max(entry.height, G.minNodeHeight)
          : G.emptyNodeHeight,
      pillAnchor: pillAnchorFor(node)
    };
  });

  const links: LaidOutLink[] = [...entries]
    .sort((a, b) => RENDER_ORDER[a.link.kind] - RENDER_ORDER[b.link.kind])
    .map((entry) => ({
      id: linkId(entry.link),
      link: entry.link,
      fromId: entry.link.from_id,
      toId: entry.link.to_id,
      path: ribbonPath(
        entry,
        working.get(entry.link.from_id)!,
        working.get(entry.link.to_id)!,
        columnX
      )
    }));

  return {
    width,
    height: Math.ceil(lowestPoint + G.paddingBottom),
    nodes,
    links
  };
};

// Longer skips leave the bar highest, so bridges never cross each other.
const outOrder = (graph: JourneyGraph, entry: WorkingLink): number => {
  if (entry.link.kind === "skip")
    return -1 - (graph.nodeById.get(entry.link.to_id)?.rank ?? 0) / 100;
  if (entry.link.kind === "drop_off")
    return (
      OUT_ORDER.drop_off +
      graph.outcomes.findIndex((o) => o.id === entry.link.to_id) / 100
    );
  return OUT_ORDER[entry.link.kind];
};

// Entry ribbons keep the order the server sent the sources in, so the legend
// order and the stack order agree.
const inOrder = (graph: JourneyGraph, entry: WorkingLink): number => {
  if (entry.link.kind === "source")
    return graph.sources.findIndex((s) => s.id === entry.link.from_id);
  if (entry.link.kind === "skip")
    return -1 + (graph.nodeById.get(entry.link.from_id)?.rank ?? 0) / 100;
  return 0;
};

export const formatJourneyRate = formatRate;
